/**
 * TODO(workplace-requests): MOCK OVERLAY — remove once the backend supports these fields.
 *
 * The Workplace Requests Settings rebuild ships against the final intended API shape, but
 * several ticket-type fields are not backed by the API yet (see
 * docs/workplace-requests-backend-api-gaps.md). This module persists those mock-only fields
 * in `localStorage` (per browser) and overlays them onto real API responses, so the
 * settings UI behaves as if the API already supported them.
 *
 * When a gap is closed on the backend, delete the corresponding overlay/extract logic and
 * the related `// TODO(workplace-requests)` seams in the endpoint clients.
 */
import {
  CreateWorkplaceRequestTypeParams,
  UpdateWorkplaceRequestTypeParams,
  WorkplaceRequestTypeElement,
  WorkplaceRequestTypeMockFields
} from "../types/workplace-request-types";

const TYPE_MOCK_KEY = "tactic:wr-mock:types";

type StoredTypeMock = WorkplaceRequestTypeMockFields;

type TypeMockStore = Record<string, StoredTypeMock>;

const isBrowser = (): boolean =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

function readStore<T>(key: string): T {
  if (!isBrowser()) {
    return {} as T;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : ({} as T);
  } catch {
    return {} as T;
  }
}

function writeStore<T>(key: string, value: T): void {
  if (!isBrowser()) {
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota / serialization errors — mock persistence is best-effort.
  }
}

function defaultedMock(
  stored: StoredTypeMock
): Required<WorkplaceRequestTypeMockFields> {
  return {
    visibility_category_ids: stored.visibility_category_ids ?? [],
    notification_rules: stored.notification_rules ?? []
  };
}

/**
 * Overlay persisted mock fields (visibility, notifications) onto a real ticket type
 * response.
 */
export function applyTypeMockOverlay(
  type: WorkplaceRequestTypeElement
): WorkplaceRequestTypeElement {
  const store = readStore<TypeMockStore>(TYPE_MOCK_KEY);
  const stored = store[type.id] ?? {};
  const mock = defaultedMock(stored);

  return {
    ...type,
    ...mock
  };
}

export function applyTypeMockOverlayList(
  types: WorkplaceRequestTypeElement[]
): WorkplaceRequestTypeElement[] {
  return types.map(applyTypeMockOverlay);
}

/**
 * Pull the mock-only fields out of create/update params and persist them for a type id.
 * Safe to call with params that contain no mock fields (it merges, never clobbers).
 */
export function persistTypeMockFromParams(
  typeId: string,
  params: CreateWorkplaceRequestTypeParams | UpdateWorkplaceRequestTypeParams
): void {
  const store = readStore<TypeMockStore>(TYPE_MOCK_KEY);
  const prev = store[typeId] ?? {};
  const next: StoredTypeMock = { ...prev };

  if ("visibility_category_ids" in params)
    next.visibility_category_ids = params.visibility_category_ids ?? [];
  if ("notification_rules" in params)
    next.notification_rules = params.notification_rules ?? [];

  store[typeId] = next;
  writeStore(TYPE_MOCK_KEY, store);
}

export function clearTypeMock(typeId: string): void {
  const store = readStore<TypeMockStore>(TYPE_MOCK_KEY);
  if (store[typeId]) {
    delete store[typeId];
    writeStore(TYPE_MOCK_KEY, store);
  }
}
